import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ApolloError, useReactiveVar } from "@apollo/client";
import CustomStylesForSaleOperations from "@services/ui-components/src/ReactSelect/CustomStylesForSaleOperations";
import { ErrorMessage, FormikErrors, FormikTouched, getIn, useFormikContext } from "formik";
import ErrorService from "../../../../../services/ErrorService";
import {
    useCalculateAmountAndCheckStockLazyQuery,
    useGetProductsForSaleQuery,
    useGetStorageByProductIdSaleLazyQuery,
} from "@me-team/host/main/salesOperation/graphql/salesOperation.hooks";
import { StockStatus } from "../../../../Products/types";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import {
    ExtraOption,
    Option,
    ProductExtraOption,
    ProductForSale,
    SellProductForm,
    WarehouseExtraOption,
} from "../../types";
import { useParams } from "react-router-dom";
import { formatOptionLabelSaleProducts, SelectableOption } from "../utils/formatOptionLabelSaleProducts";
import { ProductProxy } from "@me-team/host/main/graphql/types";
import { DeepPartial } from "../../../../../interfaces/interfaces";
import { QuantityUnitInput, ReactSelect, Spinner, TooltipCustom } from "@services/ui-components";
import { formatOptionLabelSaleWarehouse } from "../utils/formatOptionLabelSaleWarehouse";
import AmountRadioGroup from "../inputs/AmountRadioGroup";
import DiscountRadioGroup from "../inputs/DiscountRadioGroup";
import {
    debounce,
    filterOptionSaleProducts,
    getFilteredProductOptions,
    getProductFieldName,
    roundTo2Safe,
    toNumberOrNull,
} from "../utils/utils";
import { defaultTrimmedFilterOption } from "@services/ui-components/src/ReactSelect/defaultTrimmedFilterOption";
import PriceSaleInput from "../inputs/PriceSaleInput";

interface ProductSaleListTableProps {
    remove: (index: number) => ProductForSale;
    action: "create" | "update";
    disableEditing?: boolean;
    isSaleOperationReadOnly?: boolean;
}

type Row = SellProductForm["productsInput"][number];

interface RowSnapshot {
    quantity?: number;
    discount?: number;
    unit?: number;
    product?: number;
    warehouse?: number;
    amount?: string | null;
    initialAmount?: string | null;
}

const ProductSaleListTable: React.FC<ProductSaleListTableProps> = ({
    remove,
    action,
    disableEditing,
    isSaleOperationReadOnly,
}) => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number | null>(null);

    const [unitsMap, setUnitsMap] = useState<Record<number, Option[]>>({});
    const [warehousesMap, setWarehousesMap] = useState<Record<number, ExtraOption<WarehouseExtraOption>[]>>({});
    const warehousesMapRef = useRef(warehousesMap);
    const unitsMapRef = useRef(unitsMap);
    const [quantityWarnings, setQuantityWarnings] = useState<Record<number, string | null>>({});
    const [loadingCalculations, setLoadingCalculations] = useState<Record<number, boolean>>({});

    const { values, initialValues, errors, touched, setFieldValue } = useFormikContext<SellProductForm>();

    const [isDiscount, setIsDiscount] = useState<Record<number, boolean>>({});

    // Initialize the isDiscount flag for new rows
    useEffect(() => {
        setIsDiscount((prev) => {
            const updated: Record<number, boolean> = {};
            values.productsInput?.forEach((_, index) => {
                updated[index] = prev[index] ?? true;
            });
            return updated;
        });
    }, [values.productsInput]);

    const [calculateAmountAndCheckStock] = useCalculateAmountAndCheckStockLazyQuery({
        context: { errorType: "local" },
        fetchPolicy: "network-only",
        onError: (error) => setIsErrorModalOpen(ErrorService.errorHandling(error)),
    });

    const productsInputRef = useRef(values.productsInput);
    const isDiscountRef = useRef(isDiscount);

    const prevMapRef = useRef<Map<string | number, RowSnapshot>>(new Map());
    const initializedRef = useRef<Set<string | number>>(new Set());
    const mountedRef = useRef<boolean>(false);

    const getRowKey = (row: Row | DeepPartial<Row> | undefined, i: number): string | number =>
        row?.storageProductRelationId ?? row?.product ?? i;

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        productsInputRef.current = values.productsInput;
        isDiscountRef.current = isDiscount;
    }, [values.productsInput, isDiscount]);

    // keep warehousesMapRef up-to-date so debounced function can read latest options
    useEffect(() => {
        warehousesMapRef.current = warehousesMap;
    }, [warehousesMap]);

    useEffect(() => {
        unitsMapRef.current = unitsMap;
    }, [unitsMap]);

    const isProductChanged = useCallback(
        (index: number): boolean => {
            if (action !== "update") return true;
            const current = productsInputRef.current[index];
            const initial = initialValues.productsInput?.[index];
            if (!current || !initial) return false;
            const keys: (keyof typeof current)[] = ["product", "quantity", "unit", "warehouse"];
            return keys.some((key) => current[key] !== initial[key]);
        },
        [action, initialValues.productsInput]
    );

    const formatAmount = (raw: string | number | null | undefined): string | null => {
        if (raw === null || raw === undefined || raw === "") return null;
        return typeof raw === "number" ? raw.toFixed(2) : String(raw);
    };

    const debouncedCalculateAmountRef = useRef<ReturnType<typeof debounce> | null>(null);

    if (!debouncedCalculateAmountRef.current) {
        debouncedCalculateAmountRef.current = debounce(async (index: number) => {
            const product = productsInputRef.current[index];
            const key = getRowKey(productsInputRef.current?.[index] ?? values.productsInput[index], index);

            if (!mountedRef.current) return;
            if (action === "update" && !initializedRef.current.has(key)) return;
            if (!product?.product || !product?.warehouse || !product?.unit) return;

            const unitValid = (unitsMapRef.current[index] || []).some((u) => u.value === product.unit);
            if (!unitValid) {
                await setFieldValue(getProductFieldName(index, "unit"), null);
                setLoadingCalculations((prev) => ({ ...prev, [index]: false }));
                return;
            }

            const numberQty = toNumberOrNull(product.quantity);
            const allowZero = action === "update";
            if (numberQty === null) return;
            if (!allowZero && numberQty <= 0) return;

            // storageProductRelationId: either from string or from options(warehousesMapRef)
            let storageRelationId: number | null | undefined = product.storageProductRelationId;
            if (!storageRelationId) {
                const options = warehousesMapRef.current[index] || [];
                const matched = options.find((w) => w.value === product.warehouse);
                storageRelationId = matched?.storageProductRelationId ?? null;
                if (storageRelationId) {
                    await setFieldValue(getProductFieldName(index, "storageProductRelationId"), storageRelationId);
                }
            }

            if (!storageRelationId) return;

            try {
                setLoadingCalculations((prev) => ({ ...prev, [index]: true }));

                const { data } =
                    Number(product.discount) < 101 &&
                    (await calculateAmountAndCheckStock({
                        variables: {
                            branchId: Number(branchId),
                            input: {
                                storageProductRelation: storageRelationId,
                                quantity: Number(numberQty),
                                unitId: Number(product.unit),
                                discount: isDiscountRef.current[index] ? Number(product.discount) : null,
                            },
                        },
                    }));

                const response = data?.productSaleRow;
                if (!response) return;

                const isManual = !!getIn(values, getProductFieldName(index, "isAmountManuallyEdited"));

                const currentAmountRaw = productsInputRef.current?.[index]?.amount ?? null;
                const currentAmountStr = formatAmount(currentAmountRaw);

                const prevSnapshot = prevMapRef.current.get(key);
                const prevSnapshotAmountStr = prevSnapshot?.amount ?? null;

                const responseAmountFormatted =
                    response.amount !== undefined && response.amount !== null ? response.amount.toFixed(2) : null;

                const rowChanged = isProductChanged(index);

                let shouldSetAmount = false;
                if (!isManual) {
                    if (rowChanged) {
                        shouldSetAmount = true;
                    } else if (
                        !currentAmountStr ||
                        currentAmountStr === "" ||
                        currentAmountStr === prevSnapshotAmountStr
                    ) {
                        shouldSetAmount = true;
                    }
                }

                if (shouldSetAmount && responseAmountFormatted !== null) {
                    await setFieldValue(getProductFieldName(index, "amount"), responseAmountFormatted);

                    prevMapRef.current.set(key, {
                        ...(prevSnapshot || {}),
                        initialAmount: prevSnapshot?.initialAmount ?? null,
                        amount: responseAmountFormatted,
                        quantity: prevSnapshot?.quantity ?? product.quantity ?? null,
                        discount: prevSnapshot?.discount ?? product.discount ?? null,
                        unit: prevSnapshot?.unit ?? product.unit ?? null,
                        product: prevSnapshot?.product ?? product.product ?? null,
                        warehouse: prevSnapshot?.warehouse ?? product.warehouse ?? null,
                    });
                }

                if (response?.price !== undefined && response?.price !== null) {
                    await setFieldValue(getProductFieldName(index, "price"), roundTo2Safe(response.price));
                }

                if (response.isStockLimitExceeded && isProductChanged(index)) {
                    setQuantityWarnings((prev) => ({ ...prev, [index]: t("Stock exceeded") }));
                } else {
                    setQuantityWarnings((prev) => ({ ...prev, [index]: null }));
                }
            } catch (error) {
                setIsErrorModalOpen(ErrorService.errorHandling(error));
            } finally {
                setLoadingCalculations((prev) => ({ ...prev, [index]: false }));
            }
        }, 500);
    }

    useEffect(() => {
        return () => {
            debouncedCalculateAmountRef.current?.cancel();
        };
    }, []);

    const toggleRadioButton = (index: number, field: "discount" | "amount") => {
        setIsDiscount((prev) => {
            const isNowDiscount = !prev[index];
            const updated = { ...prev, [index]: isNowDiscount };

            if (!mountedRef.current) return updated;

            const key = getRowKey(values.productsInput[index], index);
            const snapshot = prevMapRef.current.get(key);
            const initialRow = initialValues?.productsInput?.[index];
            const currentRow = productsInputRef.current[index] ?? values.productsInput[index];

            const isNowAmount = !isNowDiscount;

            const formatAmtSafe = (raw: string | number | null | undefined) => formatAmount(raw);

            debouncedCalculateAmountRef.current?.cancel();

            if (field === "discount") {
                if (isNowDiscount) {
                    const amountToRestoreRaw =
                        snapshot?.initialAmount ??
                        snapshot?.amount ??
                        initialRow?.amount ??
                        (currentRow as any)?.amount ??
                        null;
                    const amountToRestore = formatAmtSafe(amountToRestoreRaw);

                    setFieldValue(getProductFieldName(index, "amount"), amountToRestore);
                    setFieldValue(getProductFieldName(index, "isAmountManuallyEdited"), false);

                    const discountToRestore =
                        snapshot?.discount ?? initialRow?.discount ?? (currentRow as any)?.discount ?? null;
                    setFieldValue(getProductFieldName(index, "discount"), discountToRestore ?? null);

                    prevMapRef.current.set(key, {
                        ...(snapshot || {}),
                        initialAmount: snapshot?.initialAmount ?? amountToRestore ?? null,
                        amount: amountToRestore ?? null,
                        discount: discountToRestore ?? null,
                    });
                } else {
                    setFieldValue(getProductFieldName(index, "discount"), null);
                    setFieldValue(getProductFieldName(index, "isAmountManuallyEdited"), false);

                    prevMapRef.current.set(key, {
                        ...(snapshot || {}),
                        discount: null,
                    });
                }
            } else {
                if (isNowAmount) {
                    setFieldValue(getProductFieldName(index, "discount"), 0);

                    const amountToRestoreRaw =
                        snapshot?.initialAmount ??
                        snapshot?.amount ??
                        initialRow?.amount ??
                        (currentRow as any)?.amount ??
                        null;
                    const amountToRestore = formatAmtSafe(amountToRestoreRaw);

                    setFieldValue(getProductFieldName(index, "amount"), amountToRestore ?? null);
                    setFieldValue(getProductFieldName(index, "isAmountManuallyEdited"), false);

                    prevMapRef.current.set(key, {
                        ...(snapshot || {}),
                        initialAmount: snapshot?.initialAmount ?? null,
                        discount: 0,
                        amount: amountToRestore ?? null,
                    });
                } else {
                    setFieldValue(getProductFieldName(index, "isAmountManuallyEdited"), true);
                }
            }

            isDiscountRef.current = updated;

            const currProduct = toNumberOrNull(currentRow.product as any);
            const currUnit = toNumberOrNull(currentRow.unit as any);
            const currWarehouse = toNumberOrNull(currentRow.warehouse as any);
            const currQty = toNumberOrNull(currentRow.quantity as any);

            const allowZero = action === "update";
            const qtyValid = currQty !== null && (allowZero ? currQty >= 0 : currQty > 0);
            const hasRequired = currProduct !== null && currUnit !== null && currWarehouse !== null && qtyValid;

            const rowChanged = isProductChanged(index);

            if (action === "update" && !rowChanged) {
                const initialAmountToSet = snapshot?.initialAmount ?? snapshot?.amount ?? initialRow?.amount ?? null;
                const formatted = formatAmtSafe(initialAmountToSet);
                const currAmountStr = formatAmtSafe(currentRow?.amount ?? null);
                if (formatted !== currAmountStr) {
                    debouncedCalculateAmountRef.current?.cancel();
                    setFieldValue(getProductFieldName(index, "amount"), formatted);
                    setFieldValue(getProductFieldName(index, "isAmountManuallyEdited"), false);
                    prevMapRef.current.set(key, {
                        ...(prevMapRef.current.get(key) || {}),
                        amount: formatted,
                    });
                }
            } else {
                if (hasRequired) {
                    debouncedCalculateAmountRef.current?.(index);
                }
            }

            return updated;
        });
    };

    const { data: productsData, loading: productsDataLoading } = useGetProductsForSaleQuery({
        variables: {
            branchId: Number(branchId),
            itemsPerPage: 10000,
            deleted: false,
            stockStatus: action === "create" ? StockStatus.InStock : null,
            id: null,
            companyId: Number(currentCompanyId),
        },
        fetchPolicy: "network-only",
        skip: !currentCompanyId,
        context: { errorType: "local" },
        onError: (error: ApolloError) => setIsErrorModalOpen(ErrorService.errorHandling(error)),
    });

    const [fetchStoragesByProductId] = useGetStorageByProductIdSaleLazyQuery({
        context: { errorType: "local" },
        onError: (error: ApolloError) => setIsErrorModalOpen(ErrorService.errorHandling(error)),
    });

    const handleProductChange = useCallback(
        async (selectedOption: Option, index: number, opts?: { skipSetPrice?: boolean }) => {
            const productId = selectedOption?.value as number;

            const prevProductId =
                productsInputRef.current?.[index]?.product ?? values.productsInput?.[index]?.product ?? null;

            const currentWarehouse = action === "update" ? values.productsInput[index]?.warehouse : null;

            await setFieldValue(getProductFieldName(index, "product"), productId);
            await setFieldValue(getProductFieldName(index, "isAmountManuallyEdited"), false);

            if (prevProductId !== productId) {
                await setFieldValue(getProductFieldName(index, "storageProductRelationId"), null);
                await setFieldValue(getProductFieldName(index, "warehouse"), null);
            }

            if (action === "create" || prevProductId !== productId) {
                await setFieldValue(getProductFieldName(index, "unit"), null);
            }

            if (!productId) {
                setUnitsMap((prev) => ({ ...prev, [index]: [] }));
                setWarehousesMap((prev) => ({ ...prev, [index]: [] }));
                await setFieldValue(getProductFieldName(index, "warehouse"), null);
                await setFieldValue(getProductFieldName(index, "unit"), null);
                await setFieldValue(getProductFieldName(index, "storageProductRelationId"), null);
                return;
            }

        try {
            const { data: storageData } = await fetchStoragesByProductId({
                variables: {
                    branchId: Number(branchId),
                    itemsPerPage: 10000,
                    deleted: false,
                    productId,
                    companyId: Number(currentCompanyId),
                },
                fetchPolicy: "network-only",
            });

                const currentProduct: DeepPartial<ProductProxy> = storageData?.products?.items[0];

                const rowChanged = isProductChanged(index);
                if (
                    !opts?.skipSetPrice &&
                    (action === "create" || rowChanged || values.productsInput?.[index]?.price == null)
                ) {
                    await setFieldValue(
                        getProductFieldName(index, "price"),
                        roundTo2Safe(Number(currentProduct.price))
                    );
                }

                const key = getRowKey(productsInputRef.current?.[index] ?? values.productsInput[index], index);
                const currentRow = productsInputRef.current?.[index] ?? values.productsInput[index];

                const rawAmount = (currentRow as any)?.amount ?? null;
                const snapshotAmount = formatAmount(rawAmount);

                const existingPrev = prevMapRef.current.get(key);

                const rowSnapshot: RowSnapshot = {
                    quantity: currentRow?.quantity ?? null,
                    discount: currentRow?.discount ?? null,
                    unit: currentRow?.unit ?? null,
                    product: currentRow?.product ?? null,
                    warehouse: currentRow?.warehouse ?? null,
                    amount: snapshotAmount,
                    initialAmount: existingPrev?.initialAmount ?? snapshotAmount,
                };

                prevMapRef.current.set(key, rowSnapshot);
                initializedRef.current.add(key);

                const shouldCalcNow = action === "create" || isProductChanged(index);
                if (shouldCalcNow) {
                    debouncedCalculateAmountRef.current?.(index);
                }

                const storageRelations = currentProduct?.storageProductRelations;
                const activeRelations = storageRelations.filter(
                    (relations) => relations?.storage && relations.storage.deleted === false
                );

                const unitsOptions: Option[] =
                    (currentProduct?.units || []).map((unit) => ({
                        value: unit.id,
                        label: unit.name,
                    })) || [];

                const warehousesOptions: ExtraOption<WarehouseExtraOption>[] =
                    activeRelations?.map((warehouse) => ({
                        value: warehouse?.storage.id,
                        label: warehouse?.storage.name,
                        stockQuantity: warehouse?.stockQuantity,
                        quantityForRender: warehouse?.quantityForRender,
                        type: warehouse?.storage?.type?.id,
                        storageProductRelationId: warehouse?.id,
                        quantity: warehouse?.quantity,
                    })) || [];

                setUnitsMap((prev) => ({ ...prev, [index]: unitsOptions }));
                setWarehousesMap((prev) => ({ ...prev, [index]: warehousesOptions }));

                const currentWarehouseExists = warehousesOptions.some((w) => w.value === currentWarehouse);
                if (currentWarehouseExists) {
                    const warehouse = warehousesOptions.find((w) => w.value === currentWarehouse);
                    await setFieldValue(
                        getProductFieldName(index, "storageProductRelationId"),
                        warehouse?.storageProductRelationId
                    );
                } else {
                    const defaultWarehouse = warehousesOptions.find((w) => w.type === 2);
                    if (defaultWarehouse) {
                        await setFieldValue(getProductFieldName(index, "warehouse"), defaultWarehouse.value);
                        await setFieldValue(
                            getProductFieldName(index, "storageProductRelationId"),
                            defaultWarehouse.storageProductRelationId
                        );
                    }
                }
            } catch (e) {
                setIsErrorModalOpen(ErrorService.errorHandling(e));
            }
        },
        [
            action,
            values.productsInput,
            setFieldValue,
            fetchStoragesByProductId,
            branchId,
            isProductChanged,
            setIsErrorModalOpen,
        ]
    );

    const productOptions = useMemo(
        () =>
            productsData?.products?.items?.map((product) => ({
                value: product?.id,
                label: product?.name,
                brand: product?.brand,
                productName: product?.name,
                sku: product?.sku,
            })) || [],
        [productsData?.products?.items]
    );

    useEffect(() => {
        const initializeMissingProductOptions = async () => {
            const itemsToInitialize = values.productsInput
                .map((product, index) => ({
                    index,
                    productId: product.product,
                    selectedOption: productOptions.find((option) => option.value === product.product),
                }))
                .filter(
                    ({ selectedOption, index, productId }) =>
                        productId && selectedOption && (!unitsMap[index]?.length || !warehousesMap[index]?.length)
                );

            if (!itemsToInitialize.length) return;

            await Promise.all(
                itemsToInitialize.map(({ selectedOption, index }) =>
                    handleProductChange(selectedOption!, index, { skipSetPrice: true })
                )
            );
        };

        if (action === "update") {
            initializeMissingProductOptions().catch((e) => setIsErrorModalOpen(ErrorService.errorHandling(e)));
        }
    }, [
        action,
        values.productsInput,
        productOptions,
        unitsMap,
        warehousesMap,
        handleProductChange,
        setIsErrorModalOpen,
    ]);

    useEffect(() => {
        const currentKeys = new Set<string | number>();

        values.productsInput.forEach((row, i) => {
            const key = getRowKey(row, i);
            currentKeys.add(key);

            const rawAmount = (row as any).amount ?? null;
            const amountFormatted = formatAmount(rawAmount);

            const prevSnapshot = prevMapRef.current.get(key);

            const rowSnapshot: RowSnapshot = {
                quantity: row.quantity,
                discount: row.discount,
                unit: row.unit,
                product: row.product,
                warehouse: row.warehouse,
                amount: amountFormatted,
                initialAmount: prevSnapshot?.initialAmount ?? amountFormatted,
            };

            if (!prevSnapshot) {
                prevMapRef.current.set(key, rowSnapshot);
                initializedRef.current.add(key);
                return;
            }

            if (!initializedRef.current.has(key)) {
                prevMapRef.current.set(key, rowSnapshot);
                initializedRef.current.add(key);
                return;
            }

            const prevQty = toNumberOrNull(prevSnapshot?.quantity);
            const currQty = toNumberOrNull(rowSnapshot.quantity);

            const prevUnit = toNumberOrNull(prevSnapshot?.unit);
            const currUnit = toNumberOrNull(rowSnapshot.unit);

            const prevProduct = toNumberOrNull(prevSnapshot?.product);
            const currProduct = toNumberOrNull(rowSnapshot.product);

            const prevWarehouse = toNumberOrNull(prevSnapshot?.warehouse);
            const currWarehouse = toNumberOrNull(rowSnapshot.warehouse);

            const prevDiscount = toNumberOrNull(prevSnapshot?.discount);
            const currDiscount = toNumberOrNull(rowSnapshot.discount);

            const changed =
                prevQty !== currQty ||
                prevUnit !== currUnit ||
                prevProduct !== currProduct ||
                prevWarehouse !== currWarehouse ||
                prevDiscount !== currDiscount;

            if (changed) {
                prevMapRef.current.set(key, {
                    ...rowSnapshot,
                    initialAmount: prevSnapshot?.initialAmount ?? rowSnapshot.initialAmount,
                });

                const allowZero = action === "update";
                const qtyValid = currQty !== null && (allowZero ? currQty >= 0 : currQty > 0);
                const hasRequired = currProduct !== null && currUnit !== null && currWarehouse !== null && qtyValid;

                if (hasRequired) {
                    debouncedCalculateAmountRef.current?.(i);
                }
            }
        });

        for (const k of Array.from(prevMapRef.current.keys())) {
            if (!currentKeys.has(k)) prevMapRef.current.delete(k);
        }
    }, [values.productsInput, action]);

    if (productsDataLoading) return <Spinner />;

    return (
        <>
            <Col className="position-relative">
                <div className="table-responsive scrollbar overflow-y-hidden overflow-x-auto rounded-1 border border-secondary">
                    <Table className="mb-0 min-w-xl" style={{ minWidth: "1080px" }} id="table">
                        <thead>
                            <tr className="fs-7">
                                <th className="py-2 col-2 ps-3 pe-1 align-middle text-start fw-bold bg-additional-grey line-height-140 ">
                                    {t("Product")}&nbsp;<span className="text-danger">*</span>
                                </th>
                                <th className="py-2 px-1 col-3 align-middle text-start fw-bold bg-additional-grey line-height-140">
                                    {t("Quantity")}&nbsp;<span className="text-danger">*</span>
                                </th>
                                <th className="py-2 px-1 col-2 align-middle text-start fw-bold bg-additional-grey line-height-140">
                                    {t("Warehouse")}&nbsp;<span className="text-danger">*</span>
                                </th>
                                <th className="py-2 px-1 col-2 align-middle text-start fw-bold bg-additional-grey line-height-140">
                                    <span>{t("Price")}</span>
                                </th>
                                <th className="py-2 px-1 w-auto align-middle text-start fw-bold bg-additional-grey line-height-140">
                                    {t("Discount")}
                                </th>
                                <th className="py-2 px-1 col-2 align-middle text-start fw-bold bg-additional-grey line-height-140">
                                    {t("Sum")}
                                </th>
                                <th className="py-2 ps-1 pe-3 pe-0 fit-content-cell fw-bold text-start bg-additional-grey align-middle"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {values?.productsInput?.map((product, index) => {
                                const productErrors = errors.productsInput?.[index] as
                                    | FormikErrors<ProductForSale>
                                    | undefined;
                                const productTouched = touched.productsInput?.[index] as
                                    | FormikTouched<ProductForSale>
                                    | undefined;
                                const selectedUnit = unitsMap[index]?.find(
                                    (unit) => unit.value === values.productsInput[index].unit
                                );
                                const unitName = selectedUnit?.label;
                                const isTooltipVisible =
                                    product.quantity != null &&
                                    product.unit != null &&
                                    product.warehouse != null &&
                                    product.product != null;

                                return (
                                    <tr key={index}>
                                        <td className="ps-3 py-2 pe-1 line-height-140 border-bottom-0">
                                            <ReactSelect
                                                id={`saleProducts-productsInput.${index}.product-field`}
                                                name={getProductFieldName(index, "product")}
                                                placeholder={t("Product name")}
                                                options={getFilteredProductOptions(index, values, productOptions)}
                                                value={
                                                    productOptions.find(
                                                        (option) =>
                                                            option.value === values.productsInput?.[index]?.product
                                                    ) || null
                                                }
                                                onChange={(selectedOption) =>
                                                    handleProductChange(selectedOption, index)
                                                }
                                                stylesSelect={CustomStylesForSaleOperations(
                                                    !!productErrors?.product,
                                                    !!productTouched?.product
                                                )}
                                                isSearchable
                                                menuPortalTarget={document.body}
                                                customFilterOption={filterOptionSaleProducts}
                                                formatOptionLabel={(
                                                    option: SelectableOption<ProductExtraOption>,
                                                    { context }
                                                ) => formatOptionLabelSaleProducts(option, { context })}
                                                disabled={loadingCalculations[index] || disableEditing}
                                            />
                                            <ErrorMessage name={getProductFieldName(index, "product")}>
                                                {(msg) => (
                                                    <div className="invalid-feedback d-block text-start">{msg}</div>
                                                )}
                                            </ErrorMessage>
                                        </td>

                                        <td className="py-2 px-1 border-bottom-0">
                                            <QuantityUnitInput
                                                quantity={{
                                                    id: `saleProducts-${getProductFieldName(index, "quantity")}-field`,
                                                    name: getProductFieldName(index, "quantity"),
                                                    placeholder: "Add quantity",
                                                    disabled:
                                                        !values.productsInput?.[index]?.product ||
                                                        loadingCalculations[index] ||
                                                        disableEditing,
                                                    allowZero: action === "update",
                                                }}
                                                unit={{
                                                    id: `saleProducts-${getProductFieldName(index, "unit")}-field`,
                                                    name: getProductFieldName(index, "unit"),
                                                    placeholder: "Select an unit",
                                                    options: unitsMap[index] || [],
                                                    value:
                                                        (unitsMap[index] || []).find(
                                                            (option) =>
                                                                option.value === values.productsInput?.[index]?.unit
                                                        ) || null,
                                                    onChange: async (selectedOption) => {
                                                        await setFieldValue(
                                                            getProductFieldName(index, "unit"),
                                                            selectedOption.value
                                                        );
                                                        await setFieldValue(
                                                            getProductFieldName(index, "isAmountManuallyEdited"),
                                                            false
                                                        );
                                                        debouncedCalculateAmountRef.current?.(index);
                                                    },
                                                    disabled:
                                                        !values.productsInput?.[index]?.product ||
                                                        loadingCalculations[index] ||
                                                        disableEditing,
                                                    stylesSelect: CustomStylesForSaleOperations(
                                                        !!productErrors?.unit,
                                                        !!productTouched?.unit,
                                                        true,
                                                        !!quantityWarnings[index]
                                                    ),
                                                }}
                                                quantityWarnings={!!quantityWarnings[index]}
                                            />
                                            {quantityWarnings[index] &&
                                                (action === "create" || isProductChanged(index)) && (
                                                    <div className="text-orange fs-7 text-start mt-1">
                                                        {quantityWarnings[index]}
                                                    </div>
                                                )}
                                        </td>

                                        <td className="py-2 px-1 line-height-140 border-bottom-0">
                                            <ReactSelect
                                                id={`saleProducts-${getProductFieldName(index, "warehouse")}-field`}
                                                name={getProductFieldName(index, "warehouse")}
                                                placeholder={t("Warehouse name")}
                                                options={warehousesMap[index] || []}
                                                value={(warehousesMap[index] || []).find(
                                                    (option) =>
                                                        option.value === values.productsInput?.[index]?.warehouse
                                                )}
                                                onChange={async (selectedOption: ExtraOption<WarehouseExtraOption>) => {
                                                    await setFieldValue(
                                                        getProductFieldName(index, "warehouse"),
                                                        selectedOption?.value
                                                    );
                                                    await setFieldValue(
                                                        getProductFieldName(index, "storageProductRelationId"),
                                                        selectedOption?.storageProductRelationId
                                                    );
                                                    await setFieldValue(
                                                        getProductFieldName(index, "isAmountManuallyEdited"),
                                                        false
                                                    );
                                                    debouncedCalculateAmountRef.current?.(index);
                                                }}
                                                stylesSelect={CustomStylesForSaleOperations(
                                                    !!productErrors?.warehouse,
                                                    !!productTouched?.warehouse
                                                )}
                                                isSearchable
                                                menuPortalTarget={document.body}
                                                disabled={
                                                    !values.productsInput?.[index]?.product ||
                                                    loadingCalculations[index] ||
                                                    disableEditing ||
                                                    (action === "update" &&
                                                        !!values.productsInput[index].storageProductRelationHistoryId)
                                                }
                                                formatOptionLabel={(option: SelectableOption<WarehouseExtraOption>) =>
                                                    formatOptionLabelSaleWarehouse(option)
                                                }
                                                customFilterOption={defaultTrimmedFilterOption}
                                            />
                                            <ErrorMessage name={getProductFieldName(index, "warehouse")}>
                                                {(msg) => (
                                                    <div className="invalid-feedback d-block text-start">{msg}</div>
                                                )}
                                            </ErrorMessage>
                                        </td>

                                        <td className="py-2 px-1 line-height-140 text-start border-bottom-0">
                                            <TooltipCustom
                                                placement="bottom"
                                                text={isTooltipVisible ? t("pricePerUnit", { unit: unitName }) : ""}
                                            >
                                                <div>
                                                    <PriceSaleInput
                                                        id={`saleProducts-${getProductFieldName(index, "price")}-field`}
                                                        name={getProductFieldName(index, "price")}
                                                        placeholder="0.00"
                                                        initialValue={
                                                            Number(values?.productsInput?.[index]?.price) || null
                                                        }
                                                        disabled
                                                    />
                                                </div>
                                            </TooltipCustom>
                                        </td>

                                        <td
                                            className="py-2 px-1 line-height-140 border-bottom-0"
                                            style={{ minWidth: "100px" }}
                                        >
                                            <DiscountRadioGroup
                                                id={`saleProducts-${getProductFieldName(index, "discount")}-field`}
                                                name={getProductFieldName(index, "discount")}
                                                index={index}
                                                isDiscount={isDiscount[index]}
                                                toggleRadioButton={() => toggleRadioButton(index, "discount")}
                                                disabled={loadingCalculations[index] || disableEditing}
                                            />
                                        </td>

                                        <td className="py-2 px-1 line-height-140 border-bottom-0">
                                            <AmountRadioGroup
                                                id={`saleProducts-${getProductFieldName(index, "amount")}-field`}
                                                name={getProductFieldName(index, "amount")}
                                                index={index}
                                                price={Number(values.productsInput?.[index]?.price) || 0}
                                                isDiscount={isDiscount[index]}
                                                toggleRadioButton={() => toggleRadioButton(index, "amount")}
                                                disabled={
                                                    loadingCalculations[index] ||
                                                    disableEditing ||
                                                    isSaleOperationReadOnly
                                                }
                                            />
                                        </td>

                                        <td className="d-flex align-items-center ps-1 pe-3 py-2 justify-content-end border-bottom-0">
                                            {!isSaleOperationReadOnly ? (
                                                <TooltipCustom
                                                    placement="bottom"
                                                    text={t("ProductsList.Delete Product")}
                                                >
                                                    <Button
                                                        variant="outline-secondary"
                                                        className="btn-square d-flex align-items-center justify-content-center p-1"
                                                        style={{ width: "49px", height: "49px" }}
                                                        onClick={() => remove(index)}
                                                        disabled={
                                                            values?.productsInput?.length < 2 ||
                                                            disableEditing ||
                                                            (action === "update" &&
                                                                !!values.productsInput[index]
                                                                    .storageProductRelationHistoryId)
                                                        }
                                                    >
                                                        <i className="bi bi-trash3 fs-16 text-dark" />
                                                    </Button>
                                                </TooltipCustom>
                                            ) : null}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>
            </Col>

            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => setIsErrorModalOpen(null)}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </>
    );
};

export default ProductSaleListTable;
